/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Download, Filter, Edit2, Trash2, Plus, FileSpreadsheet, FileText, XCircle, RotateCcw } from "lucide-react";
import * as XLSX from "xlsx";
import EditTransactionModal from "@/components/EditTransactionModal";
import DateFilterChips from "@/components/DateFilterChips";
import DangerZone from "@/components/DangerZone";

export default function ReportsPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "owner";
  const isAdmin = user?.role === "admin";
  const [editing, setEditing] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [therapistId, setTherapistId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [services, setServices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [rows, setRows] = useState([]);
  const [aggRows, setAggRows] = useState([]);       // ALL filtered rows for stats/cancelled (across pages)
  const [loadingAgg, setLoadingAgg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Build filter params from current state (single source of truth).
  // Memoized by filter state so the loader useCallbacks below have a stable
  // dependency surface (and don't recreate on every render).
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (therapistId) params.therapist_id = therapistId;
    if (serviceId) params.service_id = serviceId;
    return params;
  }, [from, to, therapistId, serviceId]);

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    const params = { ...buildFilterParams(), page: targetPage, page_size: pageSize };
    try {
      const { data } = await api.get("/transactions", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
    } finally { setLoading(false); }
  }, [buildFilterParams, pageSize]);

  // Load ALL filtered rows for aggregation (stats + cancelled section)
  const loadAgg = useCallback(async () => {
    setLoadingAgg(true);
    try {
      const params = { ...buildFilterParams(), page: 1, page_size: 500 };
      const { data } = await api.get("/transactions", { params });
      let all = [...(data.items || [])];
      const tp = data.total_pages || 1;
      for (let p = 2; p <= tp; p++) {
        const { data: more } = await api.get("/transactions", { params: { ...params, page: p } });
        all.push(...(more.items || []));
      }
      setAggRows(all);
    } finally { setLoadingAgg(false); }
  }, [buildFilterParams]);

  // Paginated table: refetch whenever page/pageSize/filters change
  useEffect(() => {
    load(page);
  }, [load, page]);

  // Aggregate stats: refetch whenever filters change (page-independent).
  // Watching filter state directly avoids the stale-state race condition
  // that happened when loadAgg was called from onClick handlers.
  useEffect(() => {
    loadAgg();
  }, [loadAgg]);

  // Explicit re-apply (used by chips / Apply button). Just reset page to 1;
  // the useEffects above will handle the actual fetching once state commits.
  function applyFilters() {
    if (page !== 1) setPage(1);
    // If already on page 1, filter useEffects will fire because from/to/etc changed.
  }

  async function fetchAllForExport() {
    const params = { page: 1, page_size: 500 };
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (therapistId) params.therapist_id = therapistId;
    if (serviceId) params.service_id = serviceId;
    const { data } = await api.get("/transactions", { params });
    // If more than 1 page, fetch all
    const allItems = [...(data.items || [])];
    const tp = data.total_pages || 1;
    for (let p = 2; p <= tp; p++) {
      const { data: more } = await api.get("/transactions", { params: { ...params, page: p } });
      allItems.push(...(more.items || []));
    }
    return allItems;
  }

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/therapists").then((r) => setTherapists(r.data));
  }, []);

  async function exportExcel() {
    const allRows = await fetchAllForExport();
    if (!allRows.length) { toast.error("Tidak ada data untuk diekspor"); return; }
    const fmtDate = (iso) => {
      try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      } catch { return iso; }
    };
    const fmtTime = (iso) => {
      try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return ""; }
    };

    const activeRows = allRows.filter((r) => !r.cancelled);
    const totalRev = activeRows.reduce((s, r) => s + (r.total || 0), 0);
    const totalSub = allRows.reduce((s, r) => s + (r.subtotal || 0), 0);
    const totalDisc = allRows.reduce((s, r) => s + (r.discount_amount || 0), 0);
    // Cash/card portions include split-payment breakdown
    const _cash = (r) => (r.cash_amount != null ? r.cash_amount : ((r.payment_method || "cash") === "card" ? 0 : r.total));
    const _card = (r) => (r.card_amount != null ? r.card_amount : (r.payment_method === "card" ? r.total : 0));
    const cashRev = activeRows.reduce((s, r) => s + _cash(r), 0);
    const cardRev = activeRows.reduce((s, r) => s + _card(r), 0);
    const cashCount = activeRows.filter((r) => _cash(r) > 0).length;
    const cardCount = activeRows.filter((r) => _card(r) > 0).length;
    const stamp = new Date().toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" });
    const period = (from || to) ? `${from || "—"} s/d ${to || "—"}` : "Semua Transaksi";

    // ── Sheet "Ringkasan" ────────────────────────────────────────────────
    const summaryRows = [
      ["JAVA SPA — LAPORAN KEUANGAN"],
      ["Prince Ahmed bin Abdulaziz Rd, King Fahd, Riyadh 11421"],
      ["+966 55 222 1465"],
      [],
      ["Periode", period],
      ["Dicetak", `${stamp} oleh ${user?.name || "—"}`],
      [],
      ["RINGKASAN"],
      ["Total Transaksi", allRows.length],
      ["Transaksi Aktif", activeRows.length],
      ["Transaksi Dibatalkan", allRows.length - activeRows.length],
      [],
      ["KEUANGAN (SAR)"],
      ["Subtotal", totalSub],
      ["Total Diskon", totalDisc],
      ["Total Pendapatan", totalRev],
      ["Rata-rata per Transaksi", activeRows.length ? totalRev / activeRows.length : 0],
      [],
      ["METODE PEMBAYARAN"],
      ["Tunai (termasuk porsi split)", cashRev, `${cashCount} tx`],
      ["Kartu (termasuk porsi split)", cardRev, `${cardCount} tx`],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary["!cols"] = [{ wch: 28 }, { wch: 32 }, { wch: 12 }];
    // Merge title row
    wsSummary["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
    ];
    // Apply formatting: title bold gold, section headers bold, numbers as currency
    const goldFill = { fgColor: { rgb: "B8901E" } };
    const lightFill = { fgColor: { rgb: "FFF8E1" } };
    const white = { color: { rgb: "FFFFFF" } };
    const bold = { font: { bold: true } };
    const styleCell = (ws, addr, style) => { if (ws[addr]) ws[addr].s = { ...(ws[addr].s || {}), ...style }; };
    styleCell(wsSummary, "A1", { font: { bold: true, sz: 16, color: { rgb: "B8901E" } }, alignment: { horizontal: "center" } });
    styleCell(wsSummary, "A2", { font: { sz: 10, color: { rgb: "777777" } }, alignment: { horizontal: "center" } });
    styleCell(wsSummary, "A3", { font: { sz: 10, color: { rgb: "777777" } }, alignment: { horizontal: "center" } });
    ["A8", "A13", "A19"].forEach((c) => styleCell(wsSummary, c, { font: { bold: true, color: { rgb: "B8901E" } }, fill: lightFill }));
    // Currency cells
    ["B14", "B15", "B16", "B17", "B20", "B21"].forEach((c) => { if (wsSummary[c]) wsSummary[c].z = '#,##0.00" SAR"'; });

    // ── Sheet "Transaksi" ────────────────────────────────────────────────
    const txHeader = [
      "No.", "Tanggal", "Jam", "No. HP", "Layanan", "Terapis", "Item",
      "Subtotal (SAR)", "Diskon (SAR)", "Total (SAR)", "Bayar", "Status", "Kasir", "Catatan",
    ];
    const txRows = allRows.map((r, i) => {
      const svcList = r.items.map((it) => it.service_name_id).join("; ");
      const thList = [...new Set(r.items.map((it) => it.therapist_name))].join("; ");
      const status = r.cancelled
        ? `Dibatalkan${r.cancel_reason ? " — " + r.cancel_reason : ""}`
        : (r.edited_by ? `Aktif (diedit oleh ${r.edited_by})` : "Aktif");
      const payLabel = (r.payment_method === "split") ? "Split" : ((r.payment_method || "cash") === "card" ? "Kartu" : "Tunai");
      return [
        i + 1,
        fmtDate(r.created_at),
        fmtTime(r.created_at),
        r.customer_phone,
        svcList,
        thList,
        r.items.length,
        Number(r.subtotal || 0),
        Number(r.discount_amount || 0),
        Number(r.total || 0),
        payLabel,
        status,
        r.cashier_name || "",
        r.cancel_notes || "",
      ];
    });
    // Total row
    const totalRow = ["", "", "", "", "", "", "TOTAL", totalSub, totalDisc, totalRev, "", "", "", ""];
    const wsTx = XLSX.utils.aoa_to_sheet([txHeader, ...txRows, [], totalRow]);
    wsTx["!cols"] = [
      { wch: 5 }, { wch: 12 }, { wch: 8 }, { wch: 16 }, { wch: 36 }, { wch: 22 },
      { wch: 6 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 28 }, { wch: 18 }, { wch: 28 },
    ];
    wsTx["!freeze"] = { xSplit: 0, ySplit: 1 };
    wsTx["!autofilter"] = { ref: `A1:N${txRows.length + 1}` };
    // Header styling
    for (let c = 0; c < txHeader.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      styleCell(wsTx, addr, {
        font: { bold: true, ...white },
        fill: { fgColor: { rgb: "B8901E" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "9B7918" } },
          bottom: { style: "thin", color: { rgb: "9B7918" } },
          left: { style: "thin", color: { rgb: "9B7918" } },
          right: { style: "thin", color: { rgb: "9B7918" } },
        },
      });
    }
    // Currency format for cols H, I, J (subtotal, diskon, total)
    for (let r = 1; r <= txRows.length; r++) {
      ["H", "I", "J"].forEach((col) => {
        const addr = `${col}${r + 1}`;
        if (wsTx[addr]) wsTx[addr].z = "#,##0.00";
      });
      // Zebra striping
      if (r % 2 === 0) {
        for (let c = 0; c < txHeader.length; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          styleCell(wsTx, addr, { fill: { fgColor: { rgb: "FBF7E6" } } });
        }
      }
    }
    // Total row styling
    const totalRowIdx = txRows.length + 2; // header(1) + rows + blank(1)
    for (let c = 0; c < totalRow.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: totalRowIdx, c });
      styleCell(wsTx, addr, {
        font: { bold: true, ...white },
        fill: { fgColor: { rgb: "6DBE45" } },
        alignment: { horizontal: c >= 7 && c <= 9 ? "right" : "center" },
      });
      if (["H", "I", "J"].includes(XLSX.utils.encode_col(c)) && wsTx[addr]) wsTx[addr].z = '#,##0.00" SAR"';
    }

    // Build workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, "Ringkasan");
    XLSX.utils.book_append_sheet(wb, wsTx, "Transaksi");

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Java-SPA_Laporan-Keuangan_${dateStr}.xlsx`, { cellStyles: true });
    toast.success("Excel berhasil diunduh");
  }

  async function exportPdf() {
    const allRows = await fetchAllForExport();
    if (!allRows.length) { toast.error("Tidak ada data untuk diekspor"); return; }
    const fmtDateTime = (iso) => {
      try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Riyadh" }); }
      catch { return iso || "—"; }
    };
    const fmtMoney = (n) => `SAR ${Number(n || 0).toFixed(2)}`;
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

    const activeRows = allRows.filter((r) => !r.cancelled);
    const totalRev = activeRows.reduce((s, r) => s + r.total, 0);
    const totalSub = allRows.reduce((s, r) => s + (r.subtotal || 0), 0);
    const totalDisc = allRows.reduce((s, r) => s + (r.discount_amount || 0), 0);
    const _cash = (r) => (r.cash_amount != null ? r.cash_amount : ((r.payment_method || "cash") === "card" ? 0 : r.total));
    const _card = (r) => (r.card_amount != null ? r.card_amount : (r.payment_method === "card" ? r.total : 0));
    const cashRev = activeRows.reduce((s, r) => s + _cash(r), 0);
    const cardRev = activeRows.reduce((s, r) => s + _card(r), 0);
    const cashCount = activeRows.filter((r) => _cash(r) > 0).length;
    const cardCount = activeRows.filter((r) => _card(r) > 0).length;
    const period = (from || to) ? `${from || "—"} s/d ${to || "—"}` : "Semua Transaksi";
    const stamp = new Date().toLocaleString("id-ID", { timeZone: "Asia/Riyadh" });

    const rowsHtml = allRows.map((r, i) => {
      const svcList = r.items.map((it) => it.service_name_id).join(", ");
      const thList = [...new Set(r.items.map((it) => it.therapist_name))].join(", ");
      const status = r.cancelled ? "DIBATALKAN" : (r.edited_by ? "Diedit" : "Aktif");
      const statusColor = r.cancelled ? "#dc2626" : (r.edited_by ? "#B8901E" : "#16a34a");
      const method = r.payment_method || "cash";
      const payLabel = method === "split" ? `Split (${Number(r.cash_amount || 0).toFixed(0)}+${Number(r.card_amount || 0).toFixed(0)})` : (method === "card" ? "Kartu" : "Tunai");
      const payColor = method === "split" ? "#B8901E" : (method === "card" ? "#B8901E" : "#16a34a");
      return `<tr ${r.cancelled ? 'style="background:#fef2f2"' : (i % 2 === 0 ? 'style="background:#fbf7e6"' : "")}>
        <td>${i + 1}</td>
        <td>${esc(fmtDateTime(r.created_at))}</td>
        <td>${esc(r.customer_phone)}</td>
        <td>${esc(svcList)}</td>
        <td>${esc(thList)}</td>
        <td style="text-align:right">${fmtMoney(r.subtotal)}</td>
        <td style="text-align:right">${fmtMoney(r.discount_amount || 0)}</td>
        <td style="text-align:right;font-weight:700;${r.cancelled ? "text-decoration:line-through;color:#dc2626" : ""}">${fmtMoney(r.total)}</td>
        <td style="text-align:center"><span style="background:${payColor}15;color:${payColor};padding:2px 8px;border-radius:999px;font-size:9px;font-weight:600">${payLabel}</span></td>
        <td style="text-align:center"><span style="color:${statusColor};font-weight:600;font-size:10px">${status}</span></td>
        <td>${esc(r.cashier_name || "")}</td>
      </tr>`;
    }).join("");

    const html = `<!doctype html>
<html lang="id"><head><meta charset="utf-8">
<title>Java SPA · Laporan Keuangan</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #111; background: #fff; padding: 16px 24px; }
  .header { border-bottom: 3px solid #B8901E; padding-bottom: 14px; margin-bottom: 18px; }
  .header h1 { margin: 0; font-size: 26px; color: #B8901E; letter-spacing: 2px; }
  .header .sub { font-size: 11px; color: #666; margin-top: 4px; }
  .meta { font-size: 11px; color: #444; margin-bottom: 18px; line-height: 1.6; }
  .summary { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin: 16px 0 24px; }
  .summary .box { background: linear-gradient(135deg, #fdf6e3, #fbf2d6); border: 1px solid #e6d495; padding: 10px 14px; border-radius: 8px; }
  .summary .box.cash { background: linear-gradient(135deg, #ecfdf5, #d1fae5); border-color: #6ee7b7; }
  .summary .box.card { background: linear-gradient(135deg, #fef3c7, #fde68a); border-color: #fcd34d; }
  .summary .label { font-size: 9px; letter-spacing: 1.5px; color: #777; text-transform: uppercase; }
  .summary .value { font-size: 17px; font-weight: 800; color: #1a1a1a; margin-top: 3px; }
  .summary .sub { font-size: 10px; color: #888; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  thead { background: linear-gradient(180deg, #B8901E, #9B7918); color: #fff; }
  thead th { padding: 9px 8px; text-align: left; font-size: 9px; letter-spacing: 1px; text-transform: uppercase; }
  tbody td { padding: 6px 8px; border-bottom: 1px solid #f0e9c8; vertical-align: top; }
  tr { page-break-inside: avoid; }
  .totals { background: #6DBE45 !important; color: #fff; font-weight: 700; }
  .totals td { padding: 10px 8px; font-size: 11px; }
  .footer { margin-top: 18px; padding-top: 10px; border-top: 1px solid #e6d495; font-size: 9px; color: #999; text-align: center; }
  .toolbar { position: sticky; top: 0; background: #fff; padding: 10px 0; margin-bottom: 12px; border-bottom: 1px solid #eee; }
  .btn { padding: 8px 18px; border-radius: 999px; background: #B8901E; color: #fff; border: 0; font-weight: 700; cursor: pointer; font-size: 13px; letter-spacing: 0.5px; }
  .btn:hover { background: #9B7918; }
  @media print { .toolbar { display: none; } body { padding: 0; } }
</style>
</head><body>
<div class="toolbar"><button class="btn" onclick="window.print()">🖨️  Cetak / Simpan PDF</button></div>
<div class="header">
  <h1>JAVA SPA · LAPORAN KEUANGAN</h1>
  <div class="sub">Prince Ahmed bin Abdulaziz Rd, King Fahd, Riyadh 11421 · +966 55 222 1465</div>
</div>
<div class="meta">
  <div><b>Periode:</b> ${esc(period)}</div>
  <div><b>Dicetak:</b> ${esc(stamp)} oleh ${esc(user?.name || "—")}</div>
</div>

<div class="summary">
  <div class="box">
    <div class="label">Total Transaksi</div>
    <div class="value">${allRows.length}</div>
    <div class="sub">${activeRows.length} aktif · ${allRows.length - activeRows.length} batal</div>
  </div>
  <div class="box">
    <div class="label">Subtotal</div>
    <div class="value">${fmtMoney(totalSub)}</div>
  </div>
  <div class="box">
    <div class="label">Diskon</div>
    <div class="value" style="color:#dc2626">−${fmtMoney(totalDisc)}</div>
  </div>
  <div class="box cash">
    <div class="label">Tunai (${cashCount} tx)</div>
    <div class="value" style="color:#16a34a">${fmtMoney(cashRev)}</div>
    <div class="sub">${totalRev > 0 ? Math.round(cashRev / totalRev * 100) : 0}% dari pendapatan</div>
  </div>
  <div class="box card">
    <div class="label">Kartu (${cardCount} tx)</div>
    <div class="value" style="color:#B8901E">${fmtMoney(cardRev)}</div>
    <div class="sub">${totalRev > 0 ? Math.round(cardRev / totalRev * 100) : 0}% dari pendapatan</div>
  </div>
</div>

<table>
  <thead><tr>
    <th>#</th><th>Tanggal & Jam</th><th>No. HP</th><th>Layanan</th><th>Terapis</th>
    <th style="text-align:right">Subtotal</th><th style="text-align:right">Diskon</th><th style="text-align:right">Total</th>
    <th style="text-align:center">Bayar</th><th style="text-align:center">Status</th><th>Kasir</th>
  </tr></thead>
  <tbody>
    ${rowsHtml}
    <tr class="totals">
      <td colspan="5" style="text-align:right">TOTAL</td>
      <td style="text-align:right">${fmtMoney(totalSub)}</td>
      <td style="text-align:right">−${fmtMoney(totalDisc)}</td>
      <td style="text-align:right">${fmtMoney(totalRev)}</td>
      <td colspan="3"></td>
    </tr>
  </tbody>
</table>

<div class="footer">Java SPA · Riyadh, Kingdom of Saudi Arabia · Dokumen internal, mohon tidak disebarkan ke pihak luar tanpa izin.</div>
<script>setTimeout(() => window.print(), 500);</script>
</body></html>`;

    const w = window.open("", "_blank");
    if (!w) { toast.error("Pop-up diblokir browser. Izinkan pop-up untuk cetak PDF."); return; }
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    w.location.replace(url);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    toast.success("PDF siap dicetak");
  }

  async function deleteTx(tx) {
    if (!window.confirm(t("tx.deleteConfirm"))) return;
    try {
      await api.delete(`/transactions/${tx.id}`);
      toast.success(t("tx.deleteSuccess"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  async function uncancelTx(tx) {
    const shortId = tx.id.slice(0, 8).toUpperCase();
    const msg =
      `Aktifkan kembali transaksi #${shortId}?\n\n` +
      `Pelanggan: ${tx.customer_phone}\n` +
      `Total: ${formatSAR(tx.total)}\n` +
      `Alasan pembatalan sebelumnya: ${tx.cancel_reason || "—"}\n\n` +
      `Transaksi akan kembali dihitung ke pendapatan. ` +
      `Riwayat pembatalan tetap disimpan sebagai catatan audit.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/transactions/${tx.id}/uncancel`);
      toast.success(`Transaksi #${shortId} berhasil diaktifkan kembali`);
      load();
      loadAgg();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengaktifkan kembali transaksi");
    }
  }

  // Aggregations: use FULL filtered dataset (aggRows) — not just current page
  const activeRows = aggRows.filter((r) => !r.cancelled);
  const cancelledRows = aggRows.filter((r) => r.cancelled);
  const totalRevenue = activeRows.reduce((s, r) => s + (r.total || 0), 0);
  const cancelledTotal = cancelledRows.reduce((s, r) => s + (r.total || 0), 0);
  const _cash = (r) => (r.cash_amount != null ? r.cash_amount : ((r.payment_method || "cash") === "card" ? 0 : r.total));
  const _card = (r) => (r.card_amount != null ? r.card_amount : (r.payment_method === "card" ? r.total : 0));
  const cashRevenue = activeRows.reduce((s, r) => s + _cash(r), 0);
  const cardRevenue = activeRows.reduce((s, r) => s + _card(r), 0);
  const cashCount = activeRows.filter((r) => _cash(r) > 0).length;
  const cardCount = activeRows.filter((r) => _card(r) > 0).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— Reports</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("reports.title")}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManage && (
            <Link
              to="/app/kasir"
              data-testid="add-transaction-btn"
              className="btn-leaf text-sm py-2"
              title="Tambah Transaksi"
            >
              <Plus className="h-4 w-4" /> Tambah Transaksi
            </Link>
          )}
          <button onClick={exportExcel} data-testid="export-excel-btn" className="btn-leaf text-sm py-2" title="Ekspor ke Excel (.xlsx)">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button onClick={exportPdf} data-testid="export-pdf-btn" className="btn-outline-gold text-sm py-2" title="Cetak / Simpan sebagai PDF">
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="card-luxury p-5 space-y-4">
        <DateFilterChips
          from={from}
          to={to}
          onChange={(f, tt) => {
            setFrom(f); setTo(tt);
            if (page !== 1) setPage(1);
          }}
          onClear={() => {
            setFrom(""); setTo("");
            if (page !== 1) setPage(1);
          }}
        />
        <div className="grid md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("reports.from")}</label>
            <input type="date" data-testid="filter-from" value={from} onChange={(e) => setFrom(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("reports.to")}</label>
            <input type="date" data-testid="filter-to" value={to} onChange={(e) => setTo(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("reports.services")}</label>
            <select data-testid="filter-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2">
              <option value="">All</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name_id}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">{t("reports.therapists")}</label>
            <select data-testid="filter-therapist" value={therapistId} onChange={(e) => setTherapistId(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2">
              <option value="">All</option>
              {therapists.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <button onClick={applyFilters} data-testid="apply-filters-btn" className="btn-gold mt-5 justify-center">
            <Filter className="h-4 w-4" /> Apply
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-luxury accent-gold p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">Transactions</div>
          <div className="mt-2 text-3xl font-heading">{total}</div>
          <div className="text-[10px] text-foreground/40 mt-1">{activeRows.length} aktif · {cancelledRows.length} batal</div>
        </div>
        <div className="card-luxury accent-gold p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">Revenue{loadingAgg && " …"}</div>
          <div className="mt-2 text-3xl font-heading text-gold">{formatSAR(totalRevenue)}</div>
          <div className="text-[10px] text-foreground/40 mt-1">Semua transaksi aktif dalam filter</div>
        </div>
        <div className="card-luxury accent-leaf p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">Avg Transaction</div>
          <div className="mt-2 text-3xl font-heading text-leaf">{activeRows.length ? formatSAR(totalRevenue / activeRows.length) : formatSAR(0)}</div>
        </div>
      </div>

      {/* Cancelled section — visible when any cancelled tx in current page */}
      {cancelledRows.length > 0 && (
        <div className="card-luxury p-5 border border-red-500/25" data-testid="reports-cancelled-section">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-400" />
              <h3 className="text-sm font-heading text-red-300 uppercase tracking-[0.15em]">Transaksi Dibatalkan</h3>
            </div>
            <div className="text-xs text-foreground/60">
              {cancelledRows.length} transaksi · <span className="text-red-300">−{formatSAR(cancelledTotal)}</span> (tidak dihitung ke pendapatan)
            </div>
          </div>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50">
                <tr className="border-b border-red-500/20">
                  <th className="text-start py-2 pe-3">{t("reports.date")}</th>
                  <th className="text-start py-2 pe-3">{t("reports.customer")}</th>
                  <th className="text-start py-2 pe-3">Kasir</th>
                  <th className="text-start py-2 pe-3">Alasan</th>
                  <th className="text-end py-2 pe-3">{t("reports.total")}</th>
                  {canManage && <th className="text-end py-2">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {cancelledRows.map((c) => (
                  <tr key={c.id} className="border-b border-red-500/10 last:border-0 align-top" data-testid={`cancelled-row-${c.id}`}>
                    <td className="py-2 pe-3 text-foreground/70">{formatDate(c.created_at)}</td>
                    <td className="py-2 pe-3">{c.customer_phone}</td>
                    <td className="py-2 pe-3 text-foreground/70">{c.cashier_name || "—"}</td>
                    <td className="py-2 pe-3 text-foreground/80">
                      <div className="text-xs">{c.cancel_reason || "—"}</div>
                      {c.cancel_notes && <div className="text-[10px] text-foreground/50 italic mt-0.5">"{c.cancel_notes}"</div>}
                    </td>
                    <td className="py-2 pe-3 text-end text-red-300 line-through">{formatSAR(c.total)}</td>
                    {canManage && (
                      <td className="py-2 text-end whitespace-nowrap">
                        <button
                          onClick={() => uncancelTx(c)}
                          data-testid={`tx-uncancel-${c.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-leaf/40 text-leaf hover:bg-leaf/10 hover:border-leaf transition active:scale-95"
                          title="Aktifkan kembali transaksi ini"
                        >
                          <RotateCcw className="h-3.5 w-3.5" /> Aktifkan Kembali
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Method Breakdown (current page) */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-luxury accent-leaf p-5" data-testid="stat-cash-revenue">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("kasir.payCash")} · Pendapatan</div>
            <span className="text-[10px] bg-leaf/15 text-leaf uppercase rounded-full px-2 py-0.5">{cashCount} tx</span>
          </div>
          <div className="mt-2 text-3xl font-heading text-leaf">{formatSAR(cashRevenue)}</div>
          <div className="text-[10px] text-foreground/40 mt-1">
            {totalRevenue > 0 ? `${Math.round((cashRevenue / totalRevenue) * 100)}% dari total pendapatan` : "—"}
          </div>
        </div>
        <div className="card-luxury accent-gold p-5" data-testid="stat-card-revenue">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-foreground/50">{t("kasir.payCard")} · Pendapatan</div>
            <span className="text-[10px] bg-gold/15 text-gold uppercase rounded-full px-2 py-0.5">{cardCount} tx</span>
          </div>
          <div className="mt-2 text-3xl font-heading text-gold">{formatSAR(cardRevenue)}</div>
          <div className="text-[10px] text-foreground/40 mt-1">
            {totalRevenue > 0 ? `${Math.round((cardRevenue / totalRevenue) * 100)}% dari total pendapatan` : "—"}
          </div>
        </div>
      </div>

      <div className="card-luxury overflow-hidden">
        {/* DESKTOP / TABLET: Table */}
        <div className="desktop-table overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 bg-charcoal-900/60">
              <tr>
                <th className="text-start px-4 py-3">{t("reports.date")}</th>
                <th className="text-start px-4 py-3">{t("reports.customer")}</th>
                <th className="text-start px-4 py-3">{t("reports.services")}</th>
                <th className="text-end px-4 py-3">{t("reports.subtotal")}</th>
                <th className="text-end px-4 py-3">{t("reports.discount")}</th>
                <th className="text-end px-4 py-3">{t("reports.total")}</th>
                <th className="text-start px-4 py-3">{t("reports.cashier")}</th>
                {canManage && <th className="text-end px-4 py-3">{t("common.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={canManage ? 8 : 7} className="text-center py-8 text-foreground/40">{t("common.loading")}</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="text-center py-8 text-foreground/40">{t("common.empty")}</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className={`border-t border-charcoal-600/40 hover:bg-gold/5 ${r.cancelled ? "opacity-50" : ""}`} data-testid={`tx-row-${r.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {formatDate(r.created_at)}
                    {(() => {
                      const m = r.payment_method || "cash";
                      if (m === "split") return <span className="ms-2 text-[10px] bg-gradient-to-r from-leaf/20 to-gold/20 text-gold uppercase rounded-full px-2 py-0.5 border border-gold/30" title={`Tunai ${r.cash_amount || 0} + Kartu ${r.card_amount || 0}`}>SPLIT</span>;
                      if (m === "card") return <span className="ms-2 text-[10px] bg-gold/15 text-gold uppercase rounded-full px-2 py-0.5" title={t("kasir.paymentMethod")}>{t("kasir.payCard")}</span>;
                      return <span className="ms-2 text-[10px] bg-leaf/15 text-leaf uppercase rounded-full px-2 py-0.5" title={t("kasir.paymentMethod")}>{t("kasir.payCash")}</span>;
                    })()}
                    {r.cancelled && <span className="ms-2 text-[10px] bg-red-500/20 text-red-300 uppercase rounded-full px-2 py-0.5">Batal</span>}
                    {r.edited_by && <span className="ms-2 text-[10px] bg-gold/15 text-gold rounded-full px-2 py-0.5" title={`${t("tx.editedBy")}: ${r.edited_by}`}>Edited</span>}
                  </td>
                  <td className="px-4 py-3">{r.customer_phone}</td>
                  <td className="px-4 py-3 text-foreground/70 max-w-xs">{r.items.map((i) => i.service_name_id).join(", ")}</td>
                  <td className="px-4 py-3 text-end">{formatSAR(r.subtotal)}</td>
                  <td className="px-4 py-3 text-end text-leaf">{r.discount_amount > 0 ? `-${formatSAR(r.discount_amount)}` : "—"}</td>
                  <td className={`px-4 py-3 text-end font-medium ${r.cancelled ? "line-through text-foreground/40" : "text-gold"}`}>{formatSAR(r.total)}</td>
                  <td className="px-4 py-3 text-foreground/60">{r.cashier_name}</td>
                  {canManage && (
                    <td className="px-4 py-3 text-end whitespace-nowrap">
                      {!r.cancelled && (
                        <button
                          onClick={() => setEditing(r)}
                          data-testid={`tx-edit-${r.id}`}
                          className="p-1.5 rounded-full hover:bg-gold/10 text-gold inline-flex me-1"
                          title={t("tx.edit")}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteTx(r)}
                        data-testid={`tx-delete-${r.id}`}
                        className="p-1.5 rounded-full hover:bg-red-500/10 text-red-400 inline-flex"
                        title={t("tx.delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* MOBILE: Card list */}
        <div className="mobile-card-list p-3">
          {loading && <div className="text-center py-8 text-foreground/40">{t("common.loading")}</div>}
          {!loading && rows.length === 0 && <div className="text-center py-8 text-foreground/40">{t("common.empty")}</div>}
          {rows.map((r) => (
            <div key={r.id} className={`bg-charcoal-900/60 border rounded-xl p-4 ${r.cancelled ? "border-red-500/30 opacity-60" : "border-gold/15"}`} data-testid={`tx-mcard-${r.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="text-xs text-foreground/50">{formatDate(r.created_at)}</div>
                <div className={`text-lg font-heading ${r.cancelled ? "line-through text-foreground/40" : "text-gold"}`}>{formatSAR(r.total)}</div>
              </div>
              <div className="text-sm font-medium mb-2">📞 {r.customer_phone}</div>
              <div className="text-xs text-foreground/70 mb-3 line-clamp-2">{r.items.map((i) => i.service_name_id).join(", ")}</div>
              <div className="flex items-center justify-between text-xs pt-2 border-t border-gold/10">
                <span className="text-foreground/50">{r.cashier_name}</span>
                {r.discount_amount > 0 && <span className="text-leaf">−{formatSAR(r.discount_amount)}</span>}
              </div>
              {canManage && (
                <div className="flex gap-2 mt-2 pt-2 border-t border-gold/10">
                  {!r.cancelled && (
                    <button
                      onClick={() => setEditing(r)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 rounded-full border border-gold/40 text-gold"
                    >
                      <Edit2 className="h-3.5 w-3.5" /> {t("common.edit")}
                    </button>
                  )}
                  <button
                    onClick={() => deleteTx(r)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 rounded-full border border-red-500/40 text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="card-luxury p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="pagination">
          <div className="text-xs text-foreground/60">
            Menampilkan{" "}
            <span className="text-gold font-medium">{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}</span>{" "}
            dari <span className="text-gold font-medium">{total}</span> transaksi
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">Per halaman:</label>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
              data-testid="page-size-select"
              className="bg-charcoal-900 border border-charcoal-600 rounded-md px-2 py-1 text-sm"
            >
              {[25, 50, 100, 200].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1 || loading}
              data-testid="page-first"
              className="px-2 py-1 rounded-md border border-charcoal-600 text-xs disabled:opacity-40 hover:border-gold/40"
            >« First</button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="page-prev"
              className="px-2 py-1 rounded-md border border-charcoal-600 text-xs disabled:opacity-40 hover:border-gold/40"
            >‹ Prev</button>
            <span className="text-xs font-mono text-foreground/80 px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              data-testid="page-next"
              className="px-2 py-1 rounded-md border border-charcoal-600 text-xs disabled:opacity-40 hover:border-gold/40"
            >Next ›</button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              data-testid="page-last"
              className="px-2 py-1 rounded-md border border-charcoal-600 text-xs disabled:opacity-40 hover:border-gold/40"
            >Last »</button>
          </div>
        </div>
      )}

      {editing && (
        <EditTransactionModal
          tx={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); loadAgg(); }}
        />
      )}

      {isAdmin && <DangerZone onResetDone={load} />}
    </div>
  );
}
